import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { COMBO } from 'constants/inputTypes';
import { canEditDocumentWorkLocation, useUserWorkLocations } from '../hooks/useUserWorkLocations';

const OfferAssetWorkingLocationComponent = ({ asset, onPropertyChange, processing, disabled, addToRefs }) => {
  const { t } = useTranslation();
  const document = asset.document;
  const isNewDocument = !document.documentId;

  const userExtraInfo = useSelector(state => state.userInfo.userExtraInfoModel, shallowEqual);
  const offerHeader = useSelector(state => state.currentOffer.header, shallowEqual);
  const canEditWorkLocation = canEditDocumentWorkLocation(document);

  const [value, setValue] = useState(null);
  const { workLocations, hasActiveConfig } = useUserWorkLocations(canEditWorkLocation);

  const headerOption = useMemo(() => {
    if (userExtraInfo?.userPartnerId == null || userExtraInfo?.userDeliveryPartnerAddressId == null) return null;
    return {
      id: `${userExtraInfo.userPartnerId}_${userExtraInfo.userDeliveryPartnerAddressId}`,
      dealerId: userExtraInfo.userPartnerId,
      workLocationId: userExtraInfo.userDeliveryPartnerAddressId,
      name: `${userExtraInfo.userPartnerName} - ${userExtraInfo.userDeliveryPartnerAddressName}`
    };
  }, [userExtraInfo]);

  const options = useMemo(() => {
    const mapped = workLocations.map(x => ({
      id: `${x.dealerId}_${x.workLocationId}`,
      dealerId: x.dealerId,
      workLocationId: x.workLocationId,
      name: x.name
    }));
    if (!headerOption || mapped.some(x => x.id === headerOption.id)) return mapped;
    return [...mapped, headerOption];
  }, [workLocations, headerOption]);

  const visible = canEditWorkLocation && hasActiveConfig;
  const label = t('OfferEdit.Asset.GeneralDetails.WorkLocation');

  useEffect(() => {
    if (!visible || !isNewDocument || document.userPartnerId != null || document.workLocationId != null) return;
    if (!headerOption) return;
    onPropertyChange('userPartnerId')(headerOption.dealerId);
    onPropertyChange('workLocationId')(headerOption.workLocationId);
  }, [visible, isNewDocument, headerOption, document.userPartnerId, document.workLocationId, onPropertyChange]);

  useEffect(() => {
    if (offerHeader?.responsiblePartnerId == null || offerHeader?.workLocationId == null) return;
    setValue({
      id: `${offerHeader.responsiblePartnerId}_${offerHeader.workLocationId}`,
      dealerId: offerHeader.responsiblePartnerId,
      workLocationId: offerHeader.workLocationId,
      name: [offerHeader.responsiblePartnerName, offerHeader.workLocationName].filter(Boolean).join(' - ')
    });
  }, [offerHeader]);

  const isOptionEqualToValue = useCallback(
    (option, selected) => option.dealerId === selected?.dealerId && option.workLocationId === selected?.workLocationId,
    []
  );

  const handleChange = useCallback(
    option => {
      setValue(option);
      onPropertyChange('userPartnerId')(option?.dealerId ?? null);
      onPropertyChange('workLocationId')(option?.workLocationId ?? null);
    },
    [onPropertyChange]
  );

  if (!visible) return null;

  return (
    <Grid item xs={8} id={label} ref={ref => addToRefs?.(ref, label, COMBO)}>
      <Autocomplete
        id='offerAssetCard_workingLocation'
        fullWidth
        label={label}
        options={options}
        value={value}
        valueKey='id'
        labelKey='name'
        onChange={handleChange}
        isClearable={false}
        isSearchable={true}
        simpleValue={false}
        isOptionEqualToValue={isOptionEqualToValue}
        disabled={disabled || processing}
        required
      />
    </Grid>
  );
};

OfferAssetWorkingLocationComponent.propTypes = {
  asset: PropTypes.object.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  processing: PropTypes.bool,
  disabled: PropTypes.bool,
  addToRefs: PropTypes.func
};

export default OfferAssetWorkingLocationComponent;
